// ============================================================
//  src/hooks/useSEO.js
//  Per-page SEO using react-helmet-async
//  Usage: import useSEO from "../hooks/useSEO";
//         useSEO({ title, description, image, url });
// ============================================================

import { Helmet } from "react-helmet-async";

const BASE_URL    = "https://www.rkfarmfoods.in";
const STORE_NAME  = "RK Farm Foods";
const DEFAULT_IMG = `${BASE_URL}/images/og-image.jpg`; // place a 1200x630 image here

// ── Per-page SEO presets ──────────────────────────────────────
export const SEO_PAGES = {
  home: {
    title: `${STORE_NAME} — Organic Millets, Rice & Jaggery | Free Delivery Pan India`,
    description:
      "Buy premium organic millets, black rice, jaggery powder and cashews online. Farm-fresh, FSSAI certified, free delivery across India. Shop RK Farm Foods today.",
    url: BASE_URL,
  },
  cart: {
    title: `Your Cart — ${STORE_NAME}`,
    description: "Review your organic food items and place your order. Free delivery across India.",
    url: `${BASE_URL}/cart`,
  },
  orders: {
    title: `My Orders — ${STORE_NAME}`,
    description: "Track and manage your RK Farm Foods orders.",
    url: `${BASE_URL}/orders`,
  },
  contact: {
    title: `Contact Us — ${STORE_NAME}`,
    description:
      "Get in touch with RK Farm Foods. FSSAI certified organic food store. Call, WhatsApp or email us for any queries.",
    url: `${BASE_URL}/contact`,
  },
  signin: {
    title: `Sign In — ${STORE_NAME}`,
    description: "Sign in to your RK Farm Foods account to track orders and manage your profile.",
    url: `${BASE_URL}/signin`,
  },
  profile: {
    title: `My Profile — ${STORE_NAME}`,
    description: "Manage your RK Farm Foods account details.",
    url: `${BASE_URL}/profile`,
  },
};

// ── Build product SEO from product data ───────────────────────
export function buildProductSEO(product) {
  if (!product) return SEO_PAGES.home;
  return {
    title: `${product.name} — Buy Online | ${STORE_NAME}`,
    description: `Buy ${product.name} online at ₹${product.price}. ${product.description?.slice(0, 100)}. Free delivery across India. FSSAI certified.`,
    url: `${BASE_URL}/product/${product.id}`,
    image: product.image ? `${BASE_URL}${product.image}` : DEFAULT_IMG,
    schema: {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: product.name,
      image: product.image ? `${BASE_URL}${product.image}` : DEFAULT_IMG,
      description: product.description,
      brand: { "@type": "Brand", name: STORE_NAME },
      offers: {
        "@type": "Offer",
        priceCurrency: "INR",
        price: product.price,
        availability: product.inStock
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
        url: `${BASE_URL}/product/${product.id}`,
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: product.rating,
        reviewCount: product.reviews,
      },
    },
  };
}

// ── SEO Component ─────────────────────────────────────────────
export default function SEO({ page, product }) {
  const meta = product
    ? buildProductSEO(product)
    : SEO_PAGES[page] || SEO_PAGES.home;

  const title       = meta.title;
  const description = meta.description;
  const url         = meta.url || BASE_URL;
  const image       = meta.image || DEFAULT_IMG;

  return (
    <Helmet>
      {/* ── Basic ── */}
      <title>{title}</title>
      <meta name="description"        content={description} />
      <meta name="robots"             content="index, follow" />
      <link rel="canonical"           href={url} />

      {/* ── Open Graph (WhatsApp / Facebook preview) ── */}
      <meta property="og:type"        content={product ? "product" : "website"} />
      <meta property="og:title"       content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url"         content={url} />
      <meta property="og:image"       content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height"content="630" />
      <meta property="og:site_name"   content={STORE_NAME} />
      <meta property="og:locale"      content="en_IN" />

      {/* ── Twitter card ── */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={image} />

      {/* ── Product schema (Google rich results) ── */}
      {meta.schema && (
        <script type="application/ld+json">
          {JSON.stringify(meta.schema)}
        </script>
      )}
    </Helmet>
  );
}
